/**
 * utils/contactExport.js
 * "Ekspor Kontak" (section A, 2026-10-12) — every real contact, both lists, to one Excel file.
 *
 * Three sheets, decided with Chalton:
 *   - PUSAT and CABANG use EXACTLY the contacts-import layout (contactImportParser.js's own
 *     CABANG_COLUMNS / PUSAT_COLUMNS, reused here — never a second copy of the header), so the
 *     file can be imported again as it is. A contact's extra addresses, extra ekspedisi and any
 *     second/third salesperson go on continuation rows with Nama Toko left blank — the shape
 *     the parser already attaches to the row above (the client's original file used the same
 *     shape). Continuation rows leave Sales blank unless they carry another salesperson, so they
 *     never raise a false "possible second sales" question on re-import. The default address
 *     goes first. Sales is the person's plain NAME — the import's Pemetaan Sales step maps it
 *     back to a person.
 *   - "Detail Lengkap" has one row per contact with every field (Kota, Telepon, PKP, Label,
 *     Bisnis, Status included) for reading and checking. The import ignores any sheet other
 *     than PUSAT/CABANG, so it never gets in the way.
 *
 * What a re-import does NOT bring back (no import column): Kota (the import derives it from the
 * address, asking when it can't), Telepon, PKP, Label, CABANG NPWP, the Bisnis link (the
 * import's own grouping step proposes it again), Nonaktif, and which address was the default
 * when there are several. The import is create-only: a name already in the books is skipped,
 * never overwritten.
 *
 * Internal contacts (the app-maintained Pusat/branch transfer stand-ins) are left out — they
 * aren't real contacts. Deactivated contacts are included (Status "Nonaktif" on the detail
 * sheet). Rows are sorted by name within each list. `buildContactExport` is pure; only
 * `downloadContactExport` touches the browser (SheetJS `writeFile`).
 */
import { utils as xlsxUtils, writeFile } from "xlsx";
import { CABANG_COLUMNS, PUSAT_COLUMNS } from "./contactImportParser";
import { resolvePersonName, resolvePersonCode } from "./persons";

export const DETAIL_SHEET_NAME = "Detail Lengkap";
export const DETAIL_COLUMNS = [
  "Daftar", "Nama Toko", "Alamat", "Alamat Lain", "Kota", "Sales", "Ekspedisi", "Telepon",
  "Contact Person", "Email", "Keterangan", "NPWP", "PKP", "Label", "Bisnis", "Status",
];
const LABEL_TEXT = { supplier: "Supplier", customer: "Pelanggan", broker: "Broker" };

const text = (v) => (v == null ? "" : String(v));

/** Addresses as plain text, the default first (then the rest in stored order). */
function orderedAddresses(contact) {
  const list = (contact.addresses || []).filter((a) => a && text(a.text).trim());
  const def = list.filter((a) => a.isDefault);
  return [...def, ...list.filter((a) => !a.isDefault)].map((a) => text(a.text));
}

/** The contact's salespeople as resolvable persons, in stored order (unresolvable ids skipped). */
function salesPersons(contact, persons) {
  return (contact.personIds || [])
    .map((id) => ({ name: resolvePersonName(persons, id), code: resolvePersonCode(persons, id) }))
    .filter((p) => p.name);
}

/** One contact as rows in the import layout: a named row, then continuation rows. */
function importRows(contact, persons, withNpwp) {
  const addresses = orderedAddresses(contact);
  const sales = salesPersons(contact, persons).map((p) => p.name);
  const ekspedisi = (contact.ekspedisi || []).map(text).filter((e) => e.trim());
  const count = Math.max(1, addresses.length, sales.length, ekspedisi.length);
  const rows = [];
  for (let i = 0; i < count; i++) {
    const first = i === 0;
    const row = [
      first ? text(contact.name) : "",
      addresses[i] || "",
      sales[i] || "",
      ekspedisi[i] || "",
      first ? text(contact.contactPerson) : "",
      first ? text(contact.email) : "",
      first ? text(contact.keterangan) : "",
    ];
    if (withNpwp) row.push(first ? text(contact.npwp) : "");
    rows.push(row);
  }
  return rows;
}

function detailRow(contact, persons, businesses) {
  const [address = "", ...otherAddresses] = orderedAddresses(contact);
  const business = contact.businessId ? (businesses || []).find((b) => b.id === contact.businessId) : null;
  return [
    contact.list === "PUSAT" ? "Pusat" : "Cabang",
    text(contact.name),
    address,
    otherAddresses.join(" | "),
    text(contact.city),
    salesPersons(contact, persons).map((p) => (p.code ? `${p.name} (${p.code})` : p.name)).join(", "),
    (contact.ekspedisi || []).map(text).filter((e) => e.trim()).join(", "),
    text(contact.phone),
    text(contact.contactPerson),
    text(contact.email),
    text(contact.keterangan),
    text(contact.npwp),
    contact.pkp ? "Ya" : "Tidak",
    (contact.labels || []).map((l) => LABEL_TEXT[l] || l).join(", "),
    business ? text(business.name) : "",
    contact.deactivated ? "Nonaktif" : "Aktif",
  ];
}

const byName = (a, b) => text(a.name).localeCompare(text(b.name), "id", { sensitivity: "base" });

/** → { PUSAT, CABANG, DETAIL } — each an array of rows (row 0 = header), ready for SheetJS. */
export function buildContactExport({ contacts = [], persons = [], businesses = [] }) {
  const real = contacts.filter((c) => c && !c.internal);
  const pusat = real.filter((c) => c.list === "PUSAT").sort(byName);
  const cabang = real.filter((c) => c.list === "CABANG").sort(byName);
  return {
    PUSAT: [PUSAT_COLUMNS, ...pusat.flatMap((c) => importRows(c, persons, true))],
    CABANG: [CABANG_COLUMNS, ...cabang.flatMap((c) => importRows(c, persons, false))],
    DETAIL: [DETAIL_COLUMNS, ...[...pusat, ...cabang].map((c) => detailRow(c, persons, businesses))],
  };
}

/** "Kontak IJM 2026-10-12.xlsx" */
export function contactExportFileName(dateStr) {
  return `Kontak IJM ${dateStr}.xlsx`;
}

/** The SheetJS workbook: sheets "PUSAT", "CABANG", "Detail Lengkap". */
export function buildContactWorkbook(data) {
  const sheets = buildContactExport(data);
  const wb = xlsxUtils.book_new();
  xlsxUtils.book_append_sheet(wb, xlsxUtils.aoa_to_sheet(sheets.PUSAT), "PUSAT");
  xlsxUtils.book_append_sheet(wb, xlsxUtils.aoa_to_sheet(sheets.CABANG), "CABANG");
  xlsxUtils.book_append_sheet(wb, xlsxUtils.aoa_to_sheet(sheets.DETAIL), DETAIL_SHEET_NAME);
  return wb;
}

/** Builds the workbook and hands it to the browser as a download. Returns the file name. */
export function downloadContactExport(data, dateStr) {
  const fileName = contactExportFileName(dateStr);
  writeFile(buildContactWorkbook(data), fileName);
  return fileName;
}
